import Store from './pages/Store';
import { Lang, langNames, translations } from './i18n';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import Home from './pages/Home';
import ClassPage from './pages/Class';
import Cart from './pages/Cart';
import Mine from './pages/Mine';
import ProductDetail from './pages/ProductDetail';
import './App.css';

type Page = 'home' | 'class' | 'cart' | 'mine' | 'detail' | 'store';
type User = {
  uid: string,
  username: string
};

export type CartItem = {
  product: Product;
  quantity: number;
};

interface WindowWithEnv extends Window {
  __ENV?: {
    backendURL: string,
    sandbox: "true" | "false",
  }
}

declare global {
  interface Window {
    Pi: any;
  }
}

const _window: WindowWithEnv = window;
const backendURL = (_window.__ENV && _window.__ENV.backendURL) || 'https://api.314pi.dpdns.org';
const axiosClient = axios.create({ baseURL: `${backendURL}`, timeout: 20000, withCredentials: true });
  // 请求重试拦截器
  let retryCount = 0;
  const MAX_RETRIES = 2;

  // 响应拦截器：自动重试
  axiosClient.interceptors.response.use(
    response => {
      retryCount = 0; // 成功时重置重试计数
      return response;
    },
    async error => {
      const config = error.config;
      
      // 只对 GET 请求和网络错误进行重试
      if (!config || config.method !== 'get') {
        return Promise.reject(error);
      }
      
      // 只重试网络错误（无响应）或 5xx 服务器错误
      if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
        retryCount++;
        if (retryCount <= MAX_RETRIES) {
          console.log(`请求失败，第 ${retryCount} 次重试...`, config.url);
          // 延迟重试：1秒、2秒
          await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
          return axiosClient(config);
        }
      }
      
      retryCount = 0;
      return Promise.reject(error);
    }
  );

const config = { headers: { 'Content-Type': 'application/json' } };

export type Product = {
  id: string;
  name: string;
  image: string;
  originalPrice: number;
  piPrice: number;
  category: string;
  description: string;
  sales?: number;
  coupon?: string;
  merchant_id?: number | null;
  skus?: any[];  // ← 加上这行
};

export const categories: {name: string; icon: string}[] = [];

export const products: Product[] = [];

const buyWithPi = async (product: Product, onSuccess: () => void, onFail: () => void) => {
  // 调试开关
  // const isDebug = new URLSearchParams(window.location.search).get('debug') === '1';
  const isDebug = false; // 调试开关：改为 true 开启，保留 URL 参数代码方便以后调用
  const debugStep = (msg: string) => {
    if (isDebug) alert(msg);
  };

  const Pi = window.Pi;
  
  if (Pi && !Pi._initialized) {
    const _w: any = window;
    const isSandbox = _w.__ENV && _w.__ENV.sandbox === "true";
    Pi.init({ version: "2.0", sandbox: !!isSandbox });
  }

  if (!Pi) {
    alert('Please open in Pi Browser');
    onFail();
    return;
  }

  debugStep('Payment: start');

  const paymentData = {
    amount: Number(product.piPrice),
    memo: `Buy ${product.name}`,
    metadata: { productId: product.id },
  };

  const callbacks = {
    onReadyForServerApproval: async (paymentId: string) => {
      debugStep('Payment: approving');
      console.log('Approving paymentId:', paymentId); // 敏感信息只进 console
      try {
        await axiosClient.post('/payments/approve', { paymentId });
        debugStep('Payment: approved');
      } catch (err: any) {
        debugStep('Payment: approve failed');
        console.error('approve error:', err.response?.data || err);
        throw err;
      }
    },
    onReadyForServerCompletion: async (paymentId: string, txid: string) => {
      debugStep('Payment: completing');
      console.log('Completing paymentId:', paymentId, 'txid:', txid);
      try {
        await axiosClient.post('/payments/complete', { paymentId, txid });
        debugStep('Payment: completed');
      } catch (err: any) {
        debugStep('Payment: complete failed');
        console.error('complete error:', err.response?.data || err);
        throw err;
      }
    },
    onCancel: (paymentId: string) => {
      debugStep('Payment: cancelled');
      onFail();
    },
    onError: (error: any, paymentId: string) => {
      debugStep('Payment: error');
      console.error('Payment error:', error, paymentId);
      onFail();
    },
  };

  try {
    debugStep('Payment: creating');
    const payment = await Pi.createPayment(paymentData, callbacks);
    console.log('Payment result:', payment);
    debugStep('Payment: success');
    onSuccess();
  } catch (err: any) {
    debugStep('Payment: failed');
    console.error('createPayment error:', err);
    onFail();
  }
};

const checkoutCart = async (cartItems: CartItem[], onSuccess: () => void, onFail: () => void) => {
  // const isDebug = new URLSearchParams(window.location.search).get('debug') === '1';
  const isDebug = false; // 调试开关：改为 true 开启，保留 URL 参数代码方便以后调用
  const debugStep = (msg: string) => {
    if (isDebug) alert(msg);
  };

  const Pi = window.Pi;
  if (!Pi) {
    alert('Please open in Pi Browser');
    onFail();
    return;
  }
  if (cartItems.length === 0) {
    alert('Cart is empty');
    onFail();
    return;
  }

  const totalAmount = cartItems.reduce((sum, item) => sum + item.product.piPrice * item.quantity, 0);
  const productNames = cartItems.map(item => `${item.product.name}×${item.quantity}`).join(', ');

  debugStep('Cart: start');

  const paymentData = {
    amount: Number(totalAmount),
    memo: `Buy ${productNames}`,
    metadata: { 
      type: 'cart',
      items: cartItems.map(item => ({ productId: item.product.id, quantity: item.quantity }))
    },
  };

  const callbacks = {
    onReadyForServerApproval: async (paymentId: string) => {
      debugStep('Cart: approving');
      console.log('Cart approving:', paymentId);
      try {
        await axiosClient.post('/payments/approve', { paymentId });
        debugStep('Cart: approved');
      } catch (err: any) {
        debugStep('Cart: approve failed');
        console.error('cart approve error:', err);
        throw err;
      }
    },
    onReadyForServerCompletion: async (paymentId: string, txid: string) => {
      debugStep('Cart: completing');
      console.log('Cart completing:', paymentId, txid);
      try {
        await axiosClient.post('/payments/complete', { paymentId, txid });
        debugStep('Cart: completed');
      } catch (err: any) {
        debugStep('Cart: complete failed');
        console.error('cart complete error:', err);
        throw err;
      }
    },
    onCancel: (paymentId: string) => {
      debugStep('Cart: cancelled');
      onFail();
    },
    onError: (error: any, paymentId: string) => {
      debugStep('Cart: error');
      console.error('Cart payment error:', error);
      onFail();
    },
  };

  try {
    debugStep('Cart: creating payment');
    const payment = await Pi.createPayment(paymentData, callbacks);
    console.log('Cart payment result:', payment);
    debugStep('Cart: success');
    onSuccess();
  } catch (err: any) {
    debugStep('Cart: failed');
    console.error('Cart createPayment error:', err);
    onFail();
  }
};

export default function App() {
  const [merchants, setMerchants] = useState<any[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  const [page, setPage] = useState<Page>('home');
  const [user, setUser] = useState<User | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [paymentMsg, setPaymentMsg] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('lang') as Lang;
    if (saved && translations[saved]) return saved;
    const browserLang = navigator.language.slice(0, 2);
    const langMap: Record<string, Lang> = { zh: 'zh', ko: 'ko', ja: 'ja', es: 'es', vi: 'vi', en: 'en' };
    return langMap[browserLang] || 'en';
  });

  const t = (key: string) => translations[lang]?.[key] || translations['en']?.[key] || key;
  const changeLang = (newLang: Lang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };

  const proxyImage = (url: string) => {
    if (!url) return url;
    if (url.startsWith('data:')) return url;
    return `https://img.314pi.dpdns.org/?url=${encodeURIComponent(url)}`;
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<{name: string; icon: string}[]>([]);
  const [payMethod, setPayMethod] = useState<'pcm' | 'pi'>('pi');
  const [walletInfo, setWalletInfo] = useState<any>(null);

  // 从后端加载商品和分类
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const pRes = await axiosClient.get('/products');
        const pData = Array.isArray(pRes.data) ? pRes.data : (pRes.data.products || []);
        setProducts(pData.map((p: any) => ({
          id: String(p.id),
          name: p.name || '',
          image: p.image || '',
          originalPrice: p.originalPrice || 0,
          piPrice: p.piPrice || 1,
          category: p.category || '',
          description: p.description || '',
          sales: p.sales || 0,
          coupon: p.coupon || '',
        })));
        const cRes = await axiosClient.get('/products/categories');
        const cData = Array.isArray(cRes.data) ? cRes.data : (cRes.data.categories || []);
        setCategories(cData.map((c: any) => ({
          name: c.name || '',
          slug: c.slug || '',   // ← 加上这行！
          icon: c.icon || '',
        })));

      } catch (err) {
        console.log('Failed to load products', err);
      }
    };
    loadData();
  }, []);

    // 查询钱包状态
  const fetchWalletInfo = async () => {
    try {
      const res = await axiosClient.get('/wallet/status');
      if (res.data?.bound) {
        const balRes = await axiosClient.get('/wallet/balance');
        setWalletInfo({
          bound: true,
          balances: balRes.data?.balances || []
        });
      } else {
        setWalletInfo({ bound: false });
      }
    } catch (e) {
      console.error('fetchWalletInfo error:', e);
      setWalletInfo(null); // 出错时设为 null，不要设为 false
    }
  };

  // 在登录后或页面加载时调用
  // 用户登录后立即获取钱包状态
  useEffect(() => {
    if (user?.uid) {
      console.log('User logged in, fetching wallet...'); // 调试用
      // 延迟 1 秒，确保 session cookie 已附加
      const timer = setTimeout(() => {
        fetchWalletInfo();
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setWalletInfo(null);
    }
  }, [user?.uid]);

  // 处理未完成的支付
  React.useEffect(() => {
    const handleIncompletePayments = async () => {
      const Pi = window.Pi;
      if (!Pi) return;
      try {
        const incompletePayments = await Pi.getIncompletePayments();
        if (incompletePayments && incompletePayments.length > 0) {
          for (const payment of incompletePayments) {
            if (payment.status === 'created' && !payment.transaction) {
              await axiosClient.post('/payments/approve', { paymentId: payment.identifier });
            } else if (payment.status === 'approved' && payment.transaction) {
              await axiosClient.post('/payments/complete', {
                paymentId: payment.identifier,
                txid: payment.transaction.txid,
              });
            }
          }
        }
      } catch (err) {
        console.log('Handle incomplete payments error', err);
      }
    };
    handleIncompletePayments();
  }, []);

  React.useEffect(() => {
    const loadMerchants = async () => {
      try {
        const res = await axiosClient.get('/products/merchants');
        const data = Array.isArray(res.data) ? res.data : (res.data.merchants || []);
        setMerchants(data);
      } catch (err) {
        console.log('Failed to load merchants', err);
      }
    };
    loadMerchants();
  }, []);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setPage('detail');
  };

  const handleBuyWithPi = (product: Product) => {
    buyWithPi(
      product,
      () => {
        setPaymentMsg(t('pay.success'));
        setTimeout(() => setPaymentMsg(null), 3000);
      },
      () => {
        setPaymentMsg(t('pay.failed'));
        setTimeout(() => setPaymentMsg(null), 3000);
      }
    );
  };
  
  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      alert('购物车为空');
      return;
    }
    
    const totalAmount = cartItems.reduce((sum, item) => sum + item.product.piPrice * item.quantity, 0);
    
    // ===== PCM 余额支付 =====
    if (payMethod === 'pcm') {
      
      // 强制实时查询钱包状态（不依赖可能过期的 walletInfo）
      let currentWallet: any = null;
      try {
        const statusRes = await axiosClient.get('/wallet/status');
        if (!statusRes.data?.bound) {
          alert('⚠️ 请先合并账户并激活钱包');
          setPage('mine');
          return;
        }
        // 查询余额
        const balRes = await axiosClient.get('/wallet/balance');
        currentWallet = {
          bound: true,
          balances: balRes.data?.balances || []
        };
      } catch (e) {
        alert('钱包状态获取失败，请检查网络后重试');
        return;
      }
      
      // 检查余额
      const piBalance = currentWallet.balances.find((b: any) => b.currency === 'PI')?.balance || 0;
      if (piBalance < totalAmount) {
        alert('💰 PCM 钱包余额不足，请选择 Pi 钱包直付');
        setPayMethod('pi');
        return;
      }
      
      // 确认支付
      if (!window.confirm(`确认使用 PCM 余额支付 π${totalAmount.toFixed(2)}？`)) return;
      
      // 执行支付
      setCheckingOut(true);
      try {
        const payRes = await axiosClient.post('/wallet/pay', {
          amount: totalAmount,
          currency: 'PI',
          order_id: 'ORDER_' + Date.now(),
          app_id: 'mall',
          remark: `商城购物`
        });
        
        if (payRes.data?.success) {
          await axiosClient.post('/orders/create-paid', {
            items: cartItems,
            total: totalAmount,
            pay_method: 'pcm',
            pay_tx_id: payRes.data.tx_id || ''
          });
          
          setCartItems([]);
          setPaymentMsg('✅ 支付成功！订单已生成');
          setTimeout(() => setPaymentMsg(null), 3000);
        }
      } catch (e: any) {
        alert('❌ 支付失败：' + (e.response?.data?.error || '余额不足'));
      } finally {
        setCheckingOut(false);
      }
      
      return;
    }
    
    // ===== Pi 直付（原有逻辑不变）=====
    setCheckingOut(true);
    checkoutCart(
      cartItems,
      () => {
        setCartItems([]);
        setPaymentMsg(t('pay.success'));
        setTimeout(() => setPaymentMsg(null), 3000);
        setCheckingOut(false);
      },
      () => {
        setPaymentMsg(t('pay.failed'));
        setTimeout(() => setPaymentMsg(null), 3000);
        setCheckingOut(false);
      }
    );
  };

  const renderPage = () => {
    switch (page) {
      case 'home':
        return <Home onProductClick={handleProductClick} onMerchantClick={(id) => { setSelectedMerchantId(id); setPage('store'); }} products={products} categories={categories} merchants={merchants} t={t} lang={lang} changeLang={changeLang} />;
      case 'class':
        return <ClassPage onProductClick={handleProductClick} products={products} categories={categories} merchants={merchants} t={t} />;
      case 'cart':
        return <Cart 
                items={cartItems} 
                setItems={setCartItems}
                onCheckout={handleCheckout}
                checkingOut={checkingOut}
                t={t}
                payMethod={payMethod}
                setPayMethod={setPayMethod}
                walletInfo={walletInfo}
                axiosClient={axiosClient}  // ← 加上这行
              />
      case 'mine':
        return <Mine user={user} onSignIn={setUser} onSignOut={() => setUser(null)} axiosClient={axiosClient} products={products} t={t} lang={lang} changeLang={changeLang} />;
      case 'store':
        return selectedMerchantId ? (
          <Store
            merchantId={selectedMerchantId}
            onBack={() => setPage('home')}
            axiosClient={axiosClient}
            onProductClick={handleProductClick}
            t={t}
            lang={lang}
          />

        ) : <Home onProductClick={handleProductClick} onMerchantClick={(id) => { setSelectedMerchantId(id); setPage('store'); }} products={products} categories={categories} merchants={merchants} t={t} lang={lang} changeLang={changeLang} />;
      case 'detail':
        return selectedProduct ? (
          <ProductDetail
            product={selectedProduct}
            onBack={() => setPage('home')}
            onAddToCart={(p) => {
              setCartItems(prev => {
                const existing = prev.find(item => item.product.id === p.id);
                if (existing) {
                  return prev.map(item =>
                    item.product.id === p.id ? { ...item, quantity: item.quantity + 1 } : item
                  );
                }
                return [...prev, { product: p, quantity: 1 }];
              });
              setPaymentMsg(`${p.name} ${t('cart.added')}`);
              setTimeout(() => setPaymentMsg(null), 2000);
            }}
            onBuyWithPi={handleBuyWithPi}
            t={t}
            axiosClient={axiosClient}
                    />
        ) : <Home onProductClick={handleProductClick} onMerchantClick={(id) => { setSelectedMerchantId(id); setPage('store'); }} products={products} categories={categories} merchants={merchants} t={t} lang={lang} changeLang={changeLang} />;
      default:
        return <Home onProductClick={handleProductClick} onMerchantClick={(id) => { setSelectedMerchantId(id); setPage('store'); }} products={products} categories={categories} merchants={merchants} t={t} lang={lang} changeLang={changeLang} />;
    }
  };

  return (
    <div className="app" style={{ paddingBottom: '70px' }}>
      {paymentMsg && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: paymentMsg.includes('successful') ? '#4CAF50' : '#f44336',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          zIndex: 9999,
          fontSize: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {paymentMsg}
        </div>
      )}
      {renderPage()}
      <div onClick={() => {
        const langs: Lang[] = ['en', 'zh', 'ko', 'ja', 'es', 'vi'];
        const nextIdx = (langs.indexOf(lang) + 1) % langs.length;
        changeLang(langs[nextIdx]);
      }} style={{
        position: 'fixed',
        right: '12px',
        bottom: '80px',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: '#611f69',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        zIndex: 9999,
      }}>
        {lang.toUpperCase()}
      </div>
            <div className="tab-bar">

        <div className={`tab-item ${page === 'home' ? 'active' : ''}`} onClick={() => setPage('home')}>
          <span className="tab-icon">🏠</span>
          <span className="tab-label">{t('nav.home')}</span>
        </div>
        <div className={`tab-item ${page === 'class' ? 'active' : ''}`} onClick={() => setPage('class')}>
          <span className="tab-icon">📂</span>
          <span className="tab-label">{t('nav.class')}</span>
        </div>
        <div className={`tab-item ${page === 'cart' ? 'active' : ''}`} onClick={() => setPage('cart')}>
          <span className="tab-icon">🛒</span>
          <span className="tab-label">{t('nav.cart')}</span>
        </div>
        <div className={`tab-item ${page === 'mine' ? 'active' : ''}`} onClick={() => setPage('mine')}>
          <span className="tab-icon">👤</span>
          <span className="tab-label">{t('nav.mine')}</span>
        </div>
      </div>
    </div>
  );
}
